import { useState } from 'react';
import { ActionMenu, ActionList, TextInput } from '@primer/react';
import { PlusIcon, SearchIcon } from '@primer/octicons-react';
import styled from 'styled-components';
import { CERTIFICATE_DESCRIPTIONS, CertificateType } from '@upright/common/model/input/companies/products/ipr';
import { AddButton, ListToken } from './shared/tokens';
import { ATTRIBUTE_GUIDANCE } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';
import { SectionContainer, ItemsContainer, ItemsList } from './shared/section-styles';
import { SectionTitleWithGuidance, SectionWarning } from './shared/section-guidance';

interface Props {
  certificates: CertificateType[];
  onCertificatesChange: (certificates: CertificateType[]) => void;
  warning?: string;
}

export function Certificates({ certificates, onCertificatesChange, warning }: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const availableCertificates = Object.values(CertificateType).filter(
    cert =>
      !certificates.includes(cert) &&
      (cert.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (CERTIFICATE_DESCRIPTIONS[cert] &&
          CERTIFICATE_DESCRIPTIONS[cert].toLowerCase().includes(searchTerm.toLowerCase()))),
  );

  const addCertificate = (certificate: CertificateType) => {
    onCertificatesChange([...certificates, certificate]);
    setIsMenuOpen(false);
    setSearchTerm('');
  };

  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  const removeCertificate = (certificate: CertificateType) => {
    onCertificatesChange(certificates.filter(cert => cert !== certificate));
  };

  return (
    <SectionContainer>
      <SectionTitleWithGuidance title="Certificates" guidance={ATTRIBUTE_GUIDANCE.certificates} />
      <ItemsContainer>
        <ItemsList>
          {certificates.map(certificate => (
            <ListToken
              key={certificate}
              size="medium"
              onRemove={() => {
                removeCertificate(certificate);
              }}
              text={certificate}
            />
          ))}
          <ActionMenu open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
            <ActionMenu.Anchor>
              <AddButton size="medium" leadingVisual={PlusIcon} text="Add certificate" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="large">
              <SearchContainer>
                <TextInput
                  placeholder="Search certificates..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                  leadingVisual={SearchIcon}
                  size="small"
                />
              </SearchContainer>
              <ScrollableActionListContainer>
                <ActionList>
                  <ActionList.Group title="Select a certificate...">
                    {availableCertificates.length > 0 ? (
                      availableCertificates.map(certificate => (
                        <ActionList.Item
                          key={certificate}
                          onSelect={() => {
                            addCertificate(certificate);
                          }}
                        >
                          <strong>{certificate}</strong>
                          {CERTIFICATE_DESCRIPTIONS[certificate] && CERTIFICATE_DESCRIPTIONS[certificate] !== '' && (
                            <> - {CERTIFICATE_DESCRIPTIONS[certificate]}</>
                          )}
                        </ActionList.Item>
                      ))
                    ) : (
                      <ActionList.Item disabled>
                        {searchTerm ? 'No matching certificates found' : 'All certificates added'}
                      </ActionList.Item>
                    )}
                  </ActionList.Group>
                </ActionList>
              </ScrollableActionListContainer>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ItemsList>
      </ItemsContainer>
      <SectionWarning warning={warning} />
    </SectionContainer>
  );
}

const SearchContainer = styled.div`
  padding: var(--space__sm);
  border-bottom: 1px solid var(--borderColor-default);
`;

const ScrollableActionListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
